import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Users, Clock, Navigation, Pencil, Share2, UserPlus, ShieldCheck, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, money, formatDate, STATUS_META, EVENT_STATUS_META, whatsappUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import GoodPayerBadge from "@/components/GoodPayerBadge";

export default function EventCard({ ev, index = 0, onApplied }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [applying, setApplying] = useState(false);
  const meta = ev.my_status ? STATUS_META[ev.my_status] : null;
  const est = ev.status && ev.status !== "aberto" ? EVENT_STATUS_META[ev.status] : null;
  const dist = ev.compat?.distance_km;
  const isOwner = user?.id && ev.creator_id === user.id;
  const canEdit = isOwner && !["cancelado", "concluido"].includes(ev.status);
  const canApply = !isOwner && !ev.my_status && (ev.status === "aberto" || ev.status == null) && (ev.my_eligible_roles?.length > 0);

  const doApply = async (e) => {
    e.stopPropagation();
    if (applying) return;
    setApplying(true);
    try {
      await api.post(`/events/${ev.id}/apply`, { role_id: ev.my_eligible_roles[0] });
      toast.success("Candidatura enviada!");
      onApplied?.();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setApplying(false); }
  };

  return (
    <div data-testid={`event-card-${ev.id}`} onClick={() => navigate(`/eventos/${ev.id}`)}
      style={{ animationDelay: `${index * 50}ms` }}
      className="fade-up group cursor-pointer bg-card border border-white/[0.06] rounded-sm p-5 hover:border-primary/40 hover:-translate-y-[2px] transition-[transform,border-color] duration-200">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-bold mb-1">{formatDate(ev.date)}</div>
          {ev.origin === "EM_CIMA_DA_HORA" && <span data-testid={`express-badge-${ev.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#FF4D00]/15 text-[#FF4D00] border border-[#FF4D00]/30 mb-1 mr-1"><Zap className="h-3 w-3" />EM CIMA DA HORA</span>}
          {ev.payment_mode === "protected" && <span data-testid={`protected-badge-${ev.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/25 mb-1"><ShieldCheck className="h-3 w-3" />Pagamento protegido</span>}
          <h3 className="font-head font-semibold text-lg leading-tight group-hover:text-primary transition-colors">{ev.name}</h3>
          {ev.organizer?.good_payer && <div className="mt-1.5"><GoodPayerBadge level={ev.organizer?.payer_level} /></div>}
        </div>
        {meta ? <span className={`text-xs px-2 py-1 rounded-sm border whitespace-nowrap ${meta?.cls || ""}`}>{(meta?.label || "").split("—")[0].trim()}</span>
          : est ? <span className={`text-xs px-2 py-1 rounded-sm border ${est.cls}`}>{est.label}</span> : null}
      </div>
      <div className="mt-4 space-y-1.5 text-sm text-zinc-400">
        <div className="flex items-center gap-2"><Clock className="h-4 w-4 shrink-0" /><span className="mono">{ev.start_time} – {ev.end_time}</span></div>
        <div className="flex items-center gap-2"><MapPin className="h-4 w-4 shrink-0" /><span className="truncate">{ev.address}</span></div>
        <div className="flex items-center gap-2"><Users className="h-4 w-4 shrink-0" />{ev.confirmed_count || 0}/{ev.slots} vagas · {ev.applicants_count || 0} candidatos</div>
        {dist != null && <div className="flex items-center gap-2 text-accent"><Navigation className="h-4 w-4 shrink-0" /><span className="mono">{dist} km de você</span></div>}
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="mono text-accent font-semibold">{money(ev.cache)}</div>
          <button data-testid={`share-wa-${ev.id}`} title="Compartilhar no WhatsApp"
            onClick={(e) => { e.stopPropagation(); window.open(whatsappUrl(ev), "_blank"); }}
            className="p-1.5 rounded-sm text-emerald-400 hover:bg-emerald-500/15 transition-colors">
            <Share2 className="h-4 w-4" />
          </button>
          {canEdit && (
            <button data-testid={`edit-event-card-${ev.id}`} title="Editar evento"
              onClick={(e) => { e.stopPropagation(); navigate(`/eventos/${ev.id}/editar`); }}
              className="flex items-center gap-1 px-2 py-1.5 rounded-sm text-primary hover:bg-primary/15 transition-colors text-xs font-medium">
              <Pencil className="h-3.5 w-3.5" />Editar
            </button>
          )}
        </div>
        <div className="flex gap-1 flex-wrap justify-end">
          {(ev.instruments || []).slice(0, 3).map((i) => (
            <span key={i} className="text-[11px] px-2 py-0.5 rounded-sm bg-[#1C1C24] text-zinc-300">{i}</span>
          ))}
        </div>
      </div>
      {canApply && (
        <button data-testid={`card-apply-${ev.id}`} onClick={doApply} disabled={applying}
          className="mt-4 w-full flex items-center justify-center gap-2 h-10 rounded-lg bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20 text-sm font-semibold transition-colors disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background">
          <UserPlus className="h-4 w-4" />{applying ? "Enviando…" : "Candidatar-se"}
        </button>
      )}
    </div>
  );
}
